import type { UiNode, UiNodeInputAttributes } from '@ory/client';

import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';

/** 表示順序: 送信ボタンは入力フィールドの後に配置 */
export const ORDER = 80;

type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * Submit inputノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるsubmit inputフィールドをレンダリングします。
 * フォーム送信をトリガーするために使用されます。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#submit-input-node
 */
export function SubmitInput({ node, disabled }: Props) {
  const { translateUiText } = useNodeTranslation();
  const attrs = node.attributes as UiNodeInputAttributes;

  if (attrs.type !== 'submit') {
    return null;
  }

  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages
    ?.filter((msg) => msg.type === 'error')
    .map((msg) => translateUiText(msg));

  return (
    <VStack className="w-full mt-4 gap-0">
      <ActionButton
        type="submit"
        name={attrs.name}
        value={attrs.value as string}
        disabled={disabled ?? attrs.disabled}
        variant="default"
        className="w-full"
      >
        {label}
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && (
        <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>
      )}
    </VStack>
  );
}
